import { useEffect } from "react"

/**
 * Paint a canvas as an effect.
 * 
 * I take care of resizing the canvas's width, height, and scale to match its CSS client size.
 * 
 * @param canvas The canvas to paint.
 * @param options Options for the graphics context.
 * @param paint The function that does the actual painting.
 * @param deps The dependencies of `paint`.
 */
export function useCanvasPainter(
    canvas: HTMLCanvasElement | null,
    options: {
        alpha: boolean
    },
    paint: (gc: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => void,
    deps: React.DependencyList
) {
    useEffect(() => {
        if (!canvas) {
            return
        }

        const gc = canvas.getContext('2d', {
            alpha: options.alpha,
        })
        if (!gc) {
            throw new Error('failed to get 2d context')
        }

        const displayScale = window.devicePixelRatio
        canvas.width = displayScale * canvas.clientWidth
        canvas.height = displayScale * canvas.clientHeight

        gc.save()
        try {
            gc.scale(displayScale, displayScale)
            paint(gc, canvas)
        } finally {
            gc.restore()
        }
    }, [canvas, options.alpha, ...deps])
}
